'use client'

import { FC, useCallback } from 'react'
import styled, { useTheme } from 'styled-components'

import {
  Button,
  CircleCheck,
  EmbroideryPatch,
  ResponsiveImage,
  TextLink,
  TypeStyleBodySmall,
  TypeStyleHeadlineExtraExtraSmall,
  TypeStyleHeadlineMedium,
  TypeStyleHeadlineSmall,
  fromMd,
  timingDefault,
  timingSlow,
  untilMd,
} from '@syconium/little-miss-figgy'

import { gql } from '../../../__generated__/graphql/catalog/gql'
import { FetchCartInterstitialVariantsQuery } from '../../../__generated__/graphql/catalog/graphql'
import { useNextQuery } from '../../_hooks/useNextQuery'
import {
  useCart,
  useCartDialog,
  useCartInterstitialDialog,
} from '../../_providers/CartProvider.client'
import { useTranslation } from '../../_providers/TranslationProvider.client'

import { NextDialog, NextDialogDismiss, NextDialogProvider } from './NextDialog.client'

type CartInterstitialItem = NonNullable<
  FetchCartInterstitialVariantsQuery['productCatalogVariants']
>[number] & {
  properties: Record<string, string | undefined>
}

const fetchCartInterstitialVariants = gql(`
  query fetchCartInterstitialVariants($skus: [String!]) {
    productCatalogVariants(skus: $skus) {
      id
      sku
      images(first: 1) {
        url
      }
      group {
        title
        color {
          displayValue
        }
        primaryCategory {
          key
          analyticsId
        }
      }
      size {
        displayValue
      }
    }
  }
`)

export const CartInterstitialAndOverlay: FC = () => {
  const theme = useTheme()
  const {
    magnolia: { general },
    cart: { interstitial },
  } = useTranslation()
  const { isOpen: isInterstitialOpen, close: closeInterstitial } = useCartInterstitialDialog()
  const { open: openMinicart } = useCartDialog()
  const { cart, lastAddedItems, status: cartStatus } = useCart()

  const onViewBagClick = useCallback(() => {
    closeInterstitial()
    openMinicart()
  }, [closeInterstitial, openMinicart])

  const { data, status: queryStatus } = useNextQuery(fetchCartInterstitialVariants, {
    variables: {
      skus: lastAddedItems.map(item => item.sku),
    },
    skip: lastAddedItems.length <= 0,
  })

  const variants = data?.productCatalogVariants

  // don't render if we haven't resolved or we don't have items
  if (cartStatus !== 'resolved' || queryStatus !== 'resolved' || variants?.length === 0) {
    return null
  }

  const cartInterstitialItems: CartInterstitialItem[] = []

  lastAddedItems.forEach(item => {
    const variant = variants?.find(e => e.sku === item.sku)

    // filter out embroidery items, we don't want to display them in the interstitial
    if (variant && variant.group.primaryCategory.key !== 'EMBROIDERY') {
      cartInterstitialItems.push({
        ...variant,
        properties: item.properties,
      })
    }
  })

  const kitTitle: string | undefined =
    cartInterstitialItems.find(
      item => typeof item.properties['_kit'] === 'string' && item.properties['_kit'] !== ''
    )?.properties['_kit'] ?? undefined

  const buildItemDescriptionCopy = (
    size: string | undefined,
    color: string | undefined
  ): string | undefined => {
    if (size && color) {
      return `${size} • ${color}`
    }
    return size || color || undefined
  }

  return (
    <NextDialogProvider open={isInterstitialOpen}>
      <CartInterstitialDialog
        onClose={closeInterstitial}
        backdropTiming={timingDefault}
        dialogTiming={timingSlow}
      >
        <CartInterstitialContent>
          <Header>
            <HeaderCopy>
              {interstitial.addedToBag}
              <HeaderIconContainer>
                <CircleCheck
                  color={theme.color.fill.background}
                  fill={theme.color.stroke.info.text.on.background}
                />
              </HeaderIconContainer>
            </HeaderCopy>
            <NextDialogDismiss
              aria-label={general.close}
              variant='black-on-white-no-border'
              size='large'
            />
          </Header>
          <AddedItemsContainer>
            {kitTitle ? <KitTitle>{kitTitle}</KitTitle> : null}
            {cartInterstitialItems.map((item, index) => (
              <AddedItem key={`${item.sku}-${index}`}>
                {item.images[0]?.url ? (
                  <ItemImageWrapper>
                    <ResponsiveImage
                      alt={item.group.title}
                      src={item.images[0].url}
                      aspectRatios={{ sm: 140 / 170, md: 140 / 170 }}
                      loading='default'
                      widths={{ unit: 'px', sm: 140, md: 140 }}
                    />
                  </ItemImageWrapper>
                ) : null}
                <ItemCopyWrapper>
                  <ItemTitle>{item.group.title}</ItemTitle>
                  {item.size?.displayValue || item.group.color?.displayValue ? (
                    <ItemDescription>
                      {buildItemDescriptionCopy(
                        item.size?.displayValue,
                        item.group.color?.displayValue
                      )}
                    </ItemDescription>
                  ) : null}
                  {item.properties['_embroideryId'] ? (
                    <ItemDescription>
                      <EmbroideryPatch />
                      {interstitial.withEmbroidery}
                    </ItemDescription>
                  ) : null}
                </ItemCopyWrapper>
              </AddedItem>
            ))}
          </AddedItemsContainer>
          <CTAContainer>
            <ViewBagButton
              aria-label={interstitial.viewBag}
              variant='white-on-black-no-border'
              block
              onClick={onViewBagClick}
            >{`${interstitial.viewBag} (${cart.itemCount})`}</ViewBagButton>
            <ContinueShoppingButton
              as='button'
              aria-label={interstitial.continueShopping}
              onClick={closeInterstitial}
            >
              {interstitial.continueShopping}
            </ContinueShoppingButton>
          </CTAContainer>
        </CartInterstitialContent>
      </CartInterstitialDialog>
    </NextDialogProvider>
  )
}

const CartInterstitialDialog = styled(NextDialog)`
  display: flex;
  justify-content: center;
  max-width: none;
  margin: 0;
  background-color: ${({ theme }) => theme.color.fill.background};
  border-radius: ${({ theme }) => theme.spacing(2)};
  ${untilMd} {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100vw;
  }
  ${fromMd} {
    width: 600px;
  }
`

const CartInterstitialContent = styled.div`
  width: 100%;
  padding: ${({ theme }) => theme.spacing(6, 4)};

  ${fromMd} {
    padding: ${({ theme }) => theme.spacing(10, 16)};
  }
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
`

const HeaderCopy = styled(TypeStyleHeadlineMedium)`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  align-items: center;
  letter-spacing: 1px;

  ${fromMd} {
    letter-spacing: 1.5px;
    font-size: 28px;
  }
`

const HeaderIconContainer = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  width: ${({ theme }) => theme.spacing(4)};
  height: ${({ theme }) => theme.spacing(4)};

  ${fromMd} {
    width: ${({ theme }) => theme.spacing(5)};
    height: ${({ theme }) => theme.spacing(5)};
  }
`

const KitTitle = styled(TypeStyleHeadlineSmall)`
  display: flex;
  align-items: flex-start;
  font-size: 17px;
  letter-spacing: 0.8px;
`

const AddedItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing(4)};
  margin: ${({ theme }) => theme.spacing(6, 0)};
`

const AddedItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing(4)};
  max-height: 170px;
`

const ItemImageWrapper = styled.div`
  border-radius: ${({ theme }) => theme.spacing(2)};
  overflow: hidden;
  height: 100%;
  width: 100%;
  max-width: 140px;
  max-height: 170px;
`

const ItemCopyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  gap: ${({ theme }) => theme.spacing(1)};
`

const ItemTitle = styled(TypeStyleHeadlineExtraExtraSmall)`
  letter-spacing: 0.6px;
`

const ItemDescription = styled(TypeStyleBodySmall)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  color: ${({ theme }) => theme.color.stroke.neutral.text.secondary.on.background};
`

const CTAContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(5)};
`

const ViewBagButton = styled(Button)`
  height: 50px;
`

const ContinueShoppingButton = styled(TextLink)`
  text-transform: uppercase;
  text-decoration: underline;
  text-underline-offset: 4px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 2px;
`
